<template>
    <section class="ugc-image-upload-wrapper">
    <div
      class="ugc-image-upload-input-file-wrapper"
    >
       <input
        v-if="tmallEnabled && isMy"
        @click="setUGCImageTmall"
        :id="inputId"
        class="hide ugc-image-source"
        type="button"
        accept="image/jpg,image/jpeg,image/png"
        :name="inputId"
        :ref="inputId"
      />
      <input
        v-else
        @change="setUGCImage"
        :id="inputId"
        class="hide ugc-image-source"
        type="file"
        accept="image/jpg,image/jpeg,image/png"
        :name="inputId"
        :ref="inputId"
      />
      <div
        class="ugc-image-upload-input-file-trigger-wrapper"
      >
        <button
          id="imageUploadInputFileTrigger"
          class="ugc-uploader-label button ugc-tray-wrapper-button"
          :disabled="(disableButton || isGCC)"
          @click="onUploadClick"
        >
          <span v-if="displayIcon" class="icon icon-camera"></span>
          {{ textLabel || translations[inputLabel] }}
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import { sessionKeys } from '@/data/constants';
import geometryUpdates from '@/mixins/geometryUpdates';
import imageUpload from "@/mixins/UGC/imageUpload";

export default {
  name: "UgcImageUpload",
  mixins: [geometryUpdates, imageUpload],
  props: {
    inputId: {
      type: String,
      default: "swatch-uploader-input"
    },
    textLabel: {
      type: String,
      default: null
    },
    inputLabel: {
      type: String,
      default: "ugcUpload"
    },
    disableButton: {
      type: Boolean,
      default: false
    },
    /**
     * This prop is useful when the component is loaded in T&C modal,
     * and forceTermCondition is true. The component inside the T&C
     * modal should show the open file window.
     */
    forceUpload: {
      type: Boolean,
      default: false
    },
    displayIcon: {
      type: Boolean,
      default: true
    },
    logCustomizerEvents: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      termsConditionAccepted: false,
      previewImage: null,
      imgUrl: null,
      isReplaceTool: false,
      UGCentryPointLaunched: false
    };
  },
  computed: {
    isGCC() {
      return this.$store.getters["utils/isGCC"];
    },
    recipe_v2() {
      return this.$store.state.recipe.recipe_v2;
    },
    availableUgcFeatures() {
      return this.$store.state.ugc.availableUgcFeatures;
    },
    feature() {
      return this.$store.state.utils.queryParams.feature;
    },
    submission_id() {
      return this.$store.getters["utils/getUrlParameterByName"](
        "submission_id"
      );
    },
    pdm() {
      return this.$store.state.utils.pdm;
    }
  },
  methods: {
    setDataLayer() {
      if (this.useEcomDataLayerFormat) {
        this.$customizerDataLayer.logAnalyticsEvent({ type: this.$customizerDataLayer.types.customizerCreateYourOwn, eventLabel: 'Add your image' });
      }
    },
    /**
     * @function launchGCC
     * Gets the gcc payload from the endpoint and sets it as temp data, goes straight to size selection modal
     */
    launchGCC() {
      this.setTermsConditionAccepted();
      this.$store
        .dispatch("utils/GET_GCC", { submission_id: this.submission_id })
        .then(gcc_data => {
          if (gcc_data.status && gcc_data.status !== 200) {
            const errorMessage = JSON.stringify(gcc_data);
            throw new Error(`Error getting GCC submission data: ${errorMessage}`);
          }

          this.$store.commit("ugc/stateManager/SET_LOADING", false);
          this.$store.commit(
            "ugc/tempData/SET_RECIPE",
            this.recipe_v2
          );
          if (this.recipe_v2.components?.[this.componentName].ugc) {
            const { image_url: imagePath, image_id: temp_image_id } = this.recipe_v2.components[this.componentName].ugc.placements[0];
            this.$store.commit("ugc/tempData/SET_UGC_IMAGE", {
              imagePath,
              temp_image_id,
              imageFormat: imagePath.split(".").pop(),
            });
          } else {
            this.$store.commit("ugc/tempData/SET_UGC_IMAGE", {
              imagePath: gcc_data.ugc_image_path,
              temp_image_id: gcc_data.temp_image_id,
              imageFormat: gcc_data.ugc_image_path.split(".").pop(),
            });
          }
          this.$modal.hide("uploadModal");
          this.showOrSkipSizeSelector();
          this.$eventBus.$emit("image-finished-loading");
          this.$emit("image-loaded", true);
        })
        .catch(err => {
          console.error(err);
          this.showUploadError();
        });
    },
    onUploadAnotherFinish() {
      this.uploadImage(this.tempFile);
    },
    setTermsConditionAccepted(value = true) {
      this.termsConditionAccepted = value;
    },
    checkAndLaunchGCC() {
      if (this.submission_id && this.recipe_v2) {
        this.launchGCC();
      }
    },
    checkUGCEntryPoint() {
      const { ugcImage, ugcImageType, ugcSize } = this.$store.state.utils.queryParams;
      const secondaryImage = this.$store.state.utils.queryParams.secondaryImage || this.$store.state.utils.queryParams.cbtImage;
      const secondaryImageType = this.$store.state.utils.queryParams.secondaryImageType || this.$store.state.utils.queryParams.cbtImageType;
      const size = ugcSize ? decodeURIComponent(ugcSize) : this.pdm.ugcDefaultSize;

      if (!size || !this.recipe_v2) {
        return;
      }

      if (secondaryImage && secondaryImageType) {
        this.launchUGCEntryPoint(size, secondaryImage, secondaryImageType, true);
      } else if (ugcImage && ugcImageType) {
        this.launchUGCEntryPoint(size, ugcImage, ugcImageType);
      }
    },
    launchUGCEntryPoint(size, imgID, imgType, isSecondaryImage = false){
      this.UGCentryPointLaunched = true;
      // checks if it is holidaytool and to disable the "Upload Another" CTA in AlmostDone
      const isUgcFeature = this.availableUgcFeatures.includes(this.feature);
      this.$store.commit("ugc/LOADING_UGC_FROM_ENTRYPOINT", (isSecondaryImage || isUgcFeature) ? 2 : 1);

      const imgBucket = this.$store.state.endPoints.customizer["ugcImageFolder"];

      this.setTermsConditionAccepted();
      this.$store.commit("ugc/stateManager/SET_LOADING", false);
      this.$store.commit("ugc/tempData/SET_RECIPE", this.recipe_v2);
      this.$store.commit("ugc/tempData/SET_UGC_IMAGE", {
        imagePath: `${imgBucket}${imgID}.${imgType}`,
        temp_image_id: imgID,
        imageFormat: imgType
      });
      this.checkUgcFeatures();
      this.$eventBus.$emit("load-size-assets", size);
      this.$eventBus.$emit("image-finished-loading");
      this.$emit("image-loaded", true);
    },
    checkUgcFeatures() {
      // if the feature is part of the list, it will be used as subgroup in the recipe
      if (this.availableUgcFeatures.includes(this.feature)) {
        this.$store.commit("ugc/tempData/SET_UGC_FEATURE", this.feature);
      }
    }
  },
  mounted() {
    this.setTermsConditionAccepted(!!this.$cookies.get(sessionKeys.ugcTermsConditionsAccepted));
    this.$emit('showBackButton', false);
  },
  created() {
    this.checkAndLaunchGCC();
    this.$eventBus.$on("launch-custom-culture", this.checkAndLaunchGCC);
    this.$eventBus.$on("reset-input-file", this.onRemoved);
    this.$eventBus.$once("preloader-finished-loading", this.checkUGCEntryPoint);
    this.$eventBus.$on("ugc-modal-open", this.checkUgcFeatures);
  },
  beforeDestroy() {
    this.$eventBus.$off("launch-custom-culture", this.checkAndLaunchGCC);
    this.$eventBus.$off("reset-input-file", this.onRemoved);
    this.$eventBus.$off("ugc-modal-open", this.checkUgcFeatures);
  }
};
</script>
